import React from 'react';
import { Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ELIGIBILITY_ADVANCED_PRICE_CENTS, TPayerCandidate } from 'types/insuranceEligibility';
import { UNMAPPED_COPY } from '../copy';
import { formatCents, formatCheckedDate } from '../derive';
import type { TRoutedNotice } from '../hooks/useClientEligibilityCheck';
import s from '../s.module.less';

// 实心图标（原型 WARN_ICON）：填充色 + 白色笔画
const WARN_ICON = (
    <svg viewBox="0 0 20 20" fill="none">
        <path
            d="M9.14 2.88a1 1 0 0 1 1.72 0l8.14 14.12a1 1 0 0 1-.86 1.5H1.86a1 1 0 0 1-.86-1.5z"
            fill="currentColor"
        />
        <path d="M10 7.4v4" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
        <circle cx="10" cy="14.7" r="1.05" fill="#fff" />
    </svg>
);

type Props = {
    carrierOnFile: string;
    persona: 'managed' | 'manual';
    // advanced search 是付费动作：候选只在跑过之后存在（08/19 两态）
    candidates?: TPayerCandidate[];
    // 已选定候选并通知患者的记录行（不触发 check，UNMAPPED 状态保持到 payer ID 配好后重查）
    routedNotice?: TRoutedNotice;
    foldOpen: boolean;
    onToggleFold: () => void;
    onRunAdvanced: () => void;
    onPick: (candidate: TPayerCandidate) => void;
    // 多份 primary（10/05）：「查另一张」盒，接在 advanced search 之后
    extra?: React.ReactNode;
    // 「<carrier> · Primary/Secondary insurance」：匹配不上的是档案上哪一张（与结果卡同一行）
    planScope?: string;
};

// unmapped payer（FR4）：请求被挡在发出之前。两态：
// ① 未搜索——说明 + advanced search CTA（仅 manual 收费且价格印在按钮上；enrolled 免费）
// ② 已搜索——候选列表，点击即选定并弹患者通知（发送后流程结束，已与产品拍板不重跑 check）。
// 刻意没有 Message patient：五秒钟的搜索通常就能回答的问题，不该先开一个来回
const UnmappedPayerCard: React.FC<Props> = ({
    carrierOnFile,
    persona,
    candidates,
    routedNotice,
    foldOpen,
    onToggleFold,
    onRunAdvanced,
    onPick,
    extra,
    planScope,
}) => {
    // 没有 Last checked（请求没发出去），这一行直接放正文最上方
    const scope = planScope ? <div className={s.planScope}>{planScope}</div> : null;
    const body = candidates ? (
        <>
            {scope}
            <div className={s.infoLine}>{UNMAPPED_COPY.expWithCandidates(carrierOnFile, persona === 'manual')}</div>
            {routedNotice && (
                <div className={s.infoLine}>
                    {UNMAPPED_COPY.routedNotice(
                        routedNotice.payer.name,
                        routedNotice.payer.payerId,
                        formatCheckedDate(routedNotice.sentAt),
                    )}
                </div>
            )}
            <div className={s.payerOpts}>
                {candidates.map((c) => (
                    <button
                        key={`${c.name}-${c.payerId}`}
                        type="button"
                        className={s.payerOpt}
                        onClick={() => onPick(c)}
                    >
                        {c.name}
                        <span className={s.payerOptSub}>{UNMAPPED_COPY.candidateSub(c.payerId, c.note)}</span>
                    </button>
                ))}
            </div>
            <div className={s.billingFoot}>{UNMAPPED_COPY.footer}</div>
            {extra}
        </>
    ) : (
        <>
            {scope}
            <div className={s.infoLine}>{UNMAPPED_COPY.expNoSearch(carrierOnFile)}</div>
            <div>
                {/* 价格只对 manual 印在按钮上；enrolled 免费（2026-09-16 拍板），不弹 cost modal */}
                <Button type="primary" icon={<SearchOutlined />} onClick={onRunAdvanced}>
                    {UNMAPPED_COPY.advCta(
                        persona === 'manual' ? formatCents(ELIGIBILITY_ADVANCED_PRICE_CENTS) : undefined,
                    )}
                </Button>
            </div>
            {extra}
        </>
    );

    return (
        <div className={`${s.resCard} ${foldOpen ? s.resCardOpen : ''}`}>
            <div
                className={`${s.resBar} ${s.tone_problem}`}
                onClick={onToggleFold}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onToggleFold();
                }}
            >
                <span className={s.resLine}>
                    <span className={s.resIcon}>{WARN_ICON}</span>
                    {UNMAPPED_COPY.barLine}
                </span>
                <span className={s.resActs}>
                    <button
                        type="button"
                        className={`${s.foldToggle} ${foldOpen ? s.foldToggleOpen : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleFold();
                        }}
                    >
                        {foldOpen ? 'Less' : 'Details'}
                        <svg
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polyline points="6 8 10 12 14 8" />
                        </svg>
                    </button>
                </span>
            </div>
            <div className={`${s.resFold} ${foldOpen ? s.resFoldOpen : ''}`}>
                <div className={s.resFoldIn}>
                    <div className={s.cardBody}>{body}</div>
                </div>
            </div>
        </div>
    );
};

export default UnmappedPayerCard;
